import type {
  DomainDto,
  ExternalNetworkEntityKind,
  GatewayDetailDto,
  HostDto,
  NetBlockDto,
  NetworkServiceDto,
  SoftwareDto,
  StorageServiceDto,
} from '@terra/shared-contracts-external-network';

type ExternalAsset =
  | DomainDto
  | HostDto
  | GatewayDetailDto
  | NetworkServiceDto
  | NetBlockDto
  | SoftwareDto
  | StorageServiceDto;

export function formatSoftwareEndpointLabel({
  hostIp,
  port,
}: {
  hostIp?: string | null;
  port?: number | null;
}): string {
  const trimmedHostIp = hostIp?.trim();

  if (trimmedHostIp && port != null) {
    return `${trimmedHostIp}:${port}`;
  }

  if (trimmedHostIp) {
    return trimmedHostIp;
  }

  if (port != null) {
    return String(port);
  }

  return '';
}

function formatNetworkServiceLabel({
  port,
  applicationProtocol,
}: {
  port: number;
  applicationProtocol?: string | null;
}): string {
  return applicationProtocol ? `${port}/${applicationProtocol}` : String(port);
}

export function getExternalAssetHeaderTitle({
  entityKind,
  asset,
  fallbackTitle,
}: {
  entityKind: ExternalNetworkEntityKind;
  asset: ExternalAsset;
  fallbackTitle: string;
}): string {
  switch (entityKind) {
    case 'DOMAIN':
      return (asset as DomainDto).fqdn || fallbackTitle;
    case 'HOST':
      return (asset as HostDto).ipAddress || fallbackTitle;
    case 'GATEWAY':
      return (asset as GatewayDetailDto).hostname || fallbackTitle;
    case 'NETWORK_SERVICE': {
      const service = asset as NetworkServiceDto;

      return formatNetworkServiceLabel({
        port: service.port,
        applicationProtocol: service.applicationProtocol,
      });
    }

    case 'NETBLOCK':
      return (asset as NetBlockDto).cidr || fallbackTitle;
    case 'SOFTWARE':
      return (asset as SoftwareDto).product || fallbackTitle;
    case 'STORAGE_SERVICE':
      return (asset as StorageServiceDto).name || fallbackTitle;
    default: {
      const _exhaustive: never = entityKind;

      return _exhaustive;
    }
  }
}

export function getExternalAssetHeaderSubtitle({
  entityKind,
  asset,
}: {
  entityKind: ExternalNetworkEntityKind;
  asset: ExternalAsset | null;
}): string {
  if (!asset) {
    return '';
  }

  switch (entityKind) {
    case 'HOST': {
      const host = asset as HostDto;

      return host.hostnames[0] ?? '';
    }

    case 'GATEWAY': {
      const gateway = asset as GatewayDetailDto;

      return gateway.ips[0] ?? '';
    }

    case 'NETWORK_SERVICE': {
      const service = asset as NetworkServiceDto;

      return service.hostIp ?? service.gatewayHostname ?? '';
    }

    case 'SOFTWARE': {
      const software = asset as SoftwareDto;

      return formatSoftwareEndpointLabel({
        hostIp: software.hostIp,
        port: software.port,
      });
    }

    case 'STORAGE_SERVICE':
      return (asset as StorageServiceDto).endpointHost ?? '';
    case 'DOMAIN':
    case 'NETBLOCK':
      return '';
    default: {
      const _exhaustive: never = entityKind;

      return _exhaustive;
    }
  }
}

export function getExternalAssetListLabel({
  entityKind,
  asset,
}: {
  entityKind: ExternalNetworkEntityKind;
  asset: Record<string, unknown>;
}): string {
  switch (entityKind) {
    case 'DOMAIN':
      return String(asset.fqdn ?? 'Domain');
    case 'HOST':
      return String(asset.ipAddress ?? 'Host');
    case 'GATEWAY':
      return String(asset.hostname ?? 'Gateway');
    case 'NETWORK_SERVICE': {
      const port = asset.port;
      const protocol = asset.applicationProtocol;

      if (typeof port !== 'number') {
        return 'Service';
      }

      return typeof protocol === 'string' && protocol.length > 0
        ? `${port}/${protocol}`
        : String(port);
    }

    case 'NETBLOCK':
      return String(asset.cidr ?? 'Netblock');
    case 'SOFTWARE': {
      const product = asset.product;
      const version = asset.version;

      if (typeof product !== 'string' || product.length === 0) {
        return 'Software';
      }

      return typeof version === 'string' && version.length > 0
        ? `${product} (${version})`
        : product;
    }

    case 'STORAGE_SERVICE':
      return String(asset.name ?? 'Storage');
    default: {
      const _exhaustive: never = entityKind;

      return _exhaustive;
    }
  }
}

export function getExternalAssetListSubtitle({
  entityKind,
  asset,
}: {
  entityKind: ExternalNetworkEntityKind;
  asset: Record<string, unknown>;
}): string {
  switch (entityKind) {
    case 'HOST': {
      const hostnames = asset.hostnames;

      if (Array.isArray(hostnames) && typeof hostnames[0] === 'string') {
        return hostnames[0];
      }

      return '';
    }

    case 'GATEWAY': {
      const ips = asset.ips;

      if (Array.isArray(ips) && typeof ips[0] === 'string') {
        return ips[0];
      }

      return '';
    }

    case 'NETWORK_SERVICE': {
      if (typeof asset.hostIp === 'string' && asset.hostIp.length > 0) {
        return asset.hostIp;
      }

      if (
        typeof asset.gatewayHostname === 'string' &&
        asset.gatewayHostname.length > 0
      ) {
        return asset.gatewayHostname;
      }

      return '';
    }

    case 'SOFTWARE': {
      return formatSoftwareEndpointLabel({
        hostIp: typeof asset.hostIp === 'string' ? asset.hostIp : null,
        port: typeof asset.port === 'number' ? asset.port : null,
      });
    }

    case 'STORAGE_SERVICE':
      return typeof asset.endpointHost === 'string' ? asset.endpointHost : '';
    case 'DOMAIN':
    case 'NETBLOCK':
      return '';
    default: {
      const _exhaustive: never = entityKind;

      return _exhaustive;
    }
  }
}
